/* POVA — HIOS Depth-Effect 3D-curved wallpaper loop
 * ---------------------------------------------------------------------------
 * Ported, almost line-for-line, from the reference WallpaperLoop component's
 * useEffect (React -> vanilla). The 16 .wp-card <img>s are laid out by Liquid
 * (so the wallpapers stay editable from the block's img1..img7 settings); this
 * file only DRIVES them. Each frame we compute the four Figma-space corners of
 * every card from a camera-curve model and apply them as a CSS matrix3d, so the
 * cards ride a 3D-curved band that scrolls in a seamless, infinite loop.
 *
 * HIOS page only (gated on <main class="hios">). The RAF animation is gated on
 * prefers-reduced-motion AND an IntersectionObserver (pauses when the row is off
 * screen). Under reduced-motion the cards are still drawn once at their resting
 * positions (static, visible — no RAF). A ResizeObserver keeps the cards sized
 * to ~500*scale. The .wallpapers wrapper also gets the reference Depth section's
 * scroll parallax (scale 1.12->1, y 90->-90, opacity 0->1). The band is held
 * invisible (.wp-loop without .is-ready) until its images have loaded, then
 * fades in fully-formed so a half-loaded band never shows as gaps. Everything
 * tears down on a real navigation away (bfcache-persisted pages keep instance).
 * ------------------------------------------------------------------------- */
(function () {
  "use strict";

  var root = document.querySelector("main.hios");
  if (!root) return; // HIOS page only — hard gate.

  var loops = [].slice.call(root.querySelectorAll(".wp-loop"));
  if (!loops.length) return;

  /* Camera-curve model (fitted to the Figma layout). Embedded verbatim from
     the reference wallpaperCurve.json: model=[F,ZC,R,D,A,B,CY]. */
  var curve = {"model":[3090.8966,1305.75092,1078.8304,0.35286,163.50467,373.41551,1080.14488],"knots":[-5,-4,-3,-2,-1,0,1,2,3,4,5],"res":[[0.0,0.0,0.261,-1.445,5.224,0.935,5.428,-0.288,0.093,0.0,0.0],[0.0,0.0,10.741,-32.023,-22.026,15.877,17.712,0.874,9.386,0.0,0.0],[0.0,0.0,0.907,1.288,-4.428,0.065,-3.224,2.445,0.739,0.0,0.0],[0.0,0.0,9.394,0.883,17.712,15.877,-22.177,-32.039,10.748,0.0,0.0],[0.0,0.0,0.907,1.288,-4.428,0.065,-3.224,2.445,0.739,0.0,0.0],[0.0,0.0,-9.491,-0.917,-17.352,-16.166,22.452,31.87,-10.798,0.0,0.0],[0.0,0.0,0.261,-1.445,5.224,0.935,5.428,-0.288,0.093,0.0,0.0],[0.0,0.0,-10.788,31.84,22.301,-16.166,-17.352,-0.901,-9.474,0.0,0.0]],"m2":[[0.0,2.6713,-9.1202,22.0094,-28.6684,26.9159,-26.3046,17.0549,-5.3421,1.4747,0.0],[0.0,45.3059,-116.7751,100.7617,30.2947,-54.509,-28.6571,57.0929,-47.6151,25.9826,-0.0],[0.0,0.5773,3.1341,-16.275,25.3928,-24.0485,24.1106,-18.6457,6.2232,-0.447,-0.0],[0.0,25.9957,-47.6188,57.0484,-28.5301,-54.9211,30.9097,100.4299,-116.7368,45.3067,0.0],[0.0,0.5773,3.1341,-16.275,25.3928,-24.0485,24.1106,-18.6457,6.2232,-0.447,0.0],[0.0,-26.1373,47.6031,-55.8844,25.8801,58.0867,-33.6294,-98.7715,116.2024,-45.247,0.0],[0.0,2.6713,-9.1202,22.0094,-28.6684,26.9159,-26.3046,17.0549,-5.3421,1.4747,0.0],[0.0,-45.2301,116.1918,-99.0364,-33.0539,57.6823,26.0173,-55.9342,47.5793,-26.1054,0.0]]};

  var MODEL = curve.model, knots = curve.knots, res = curve.res, m2 = curve.m2;
  var F = MODEL[0], ZC = MODEL[1], R = MODEL[2], D = MODEL[3], A = MODEL[4], B = MODEL[5], CY = MODEL[6];

  var DESIGN_W = 3840;          // Figma width of this row
  var CARD_W = 600, CARD_H = 1333; // flattened wallpaper size
  var SPEED = 0.2;              // cards per second (constant)
  var VISIBLE_LIMIT = 4.6;      // beyond this a card is fully off-screen

  // Natural cubic spline (precomputed second derivatives) for the small correction.
  function spline(c, u) {
    if (u <= knots[0] || u >= knots[knots.length - 1]) return 0;
    var i = Math.min(knots.length - 2, Math.floor(u - knots[0]));
    var t = u - knots[i];
    var y0 = res[c][i], y1 = res[c][i + 1], M0 = m2[c][i], M1 = m2[c][i + 1];
    var s = 1 - t;
    return s * y0 + t * y1 + ((s * s * s - s) * M0 + (t * t * t - t) * M1) / 6;
  }

  // Corners [tlx,tly,trx,try,brx,bry,blx,bly] in Figma px for a card at slot u (0 = centre).
  var P = new Float64Array(8);
  var SGN = [[-1, 1], [1, 1], [1, -1], [-1, -1]];
  function corners(u) {
    var ph = u * D, cx = R * Math.sin(ph), cz = -(ZC + R * Math.cos(ph));
    var tx = Math.cos(ph), tz = Math.sin(ph);
    for (var k = 0; k < 4; k++) {
      var sx = SGN[k][0], sy = SGN[k][1];
      var depth = -(cz + sx * A * tz);
      P[k * 2] = 1920 + (F * (cx + sx * A * tx)) / depth + spline(k * 2, u);
      P[k * 2 + 1] = CY - (F * sy * B) / depth + spline(k * 2 + 1, u);
    }
    return P;
  }

  // CSS matrix3d that maps the CARD_W x CARD_H rectangle onto the quad (scaled by s).
  function quadMatrix(p, s, W, H) {
    var x0 = p[0] * s, y0 = p[1] * s, x1 = p[2] * s, y1 = p[3] * s;
    var x2 = p[4] * s, y2 = p[5] * s, x3 = p[6] * s, y3 = p[7] * s;
    var dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3;
    var dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
    var det = dx1 * dy2 - dx2 * dy1;
    var g = (dx3 * dy2 - dx2 * dy3) / det;
    var h = (dx1 * dy3 - dx3 * dy1) / det;
    var a = x1 - x0 + g * x1, b = x3 - x0 + h * x3;
    var d = y1 - y0 + g * y1, e = y3 - y0 + h * y3;
    return "matrix3d(" + (a / W) + "," + (d / W) + ",0," + (g / W) + "," +
      (b / H) + "," + (e / H) + ",0," + (h / H) + ",0,0,1,0," + x0 + "," + y0 + ",0,1)";
  }

  var reduceMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
  var teardowns = [];

  loops.forEach(function (el) { teardowns.push(startWallpaperLoop(el)); });

  // Real navigation away -> tear down. bfcache (persisted) keeps the instance.
  window.addEventListener("pagehide", function (e) {
    if (e.persisted) return;
    while (teardowns.length) { try { teardowns.pop()(); } catch (err) {} }
  });

  function startWallpaperLoop(el) {
    var wrapper = (el.closest && el.closest(".wallpapers")) || el.parentNode;
    var cards = [].slice.call(el.querySelectorAll(".wp-card"));
    var COUNT = cards.length;
    if (!COUNT) return function () {};

    // Each card is laid out close to its on-screen size so the browser resamples
    // the image smoothly; the 3D transform then only applies gentle scaling.
    var scale = 1, cw = CARD_W, ch = CARD_H;
    function resize() {
      scale = el.clientWidth / DESIGN_W;
      cw = Math.max(48, Math.round(500 * scale));
      ch = Math.round((cw * CARD_H) / CARD_W);
      for (var n = 0; n < cards.length; n++) {
        var node = cards[n];
        if (!node) continue;
        node.style.width = cw + "px";
        node.style.height = ch + "px";
        node.style.borderRadius = ((25 / CARD_W) * cw) + "px";
      }
    }
    resize();

    // Hold the band hidden until its images have loaded, then fade it in
    // fully-formed — a half-loaded band never shows as transparent gaps.
    (function revealWhenReady() {
      var pending = cards.length;
      var ready = function () { el.classList.add("is-ready"); };
      if (!pending) { ready(); return; }
      var done = function () { if (--pending <= 0) ready(); };
      cards.forEach(function (img) {
        if (img.complete && img.naturalWidth) { done(); }
        else {
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        }
      });
      setTimeout(ready, 2500); // safety: never hold the band hidden too long
    })();

    var phase = 0, last = 0, raf = 0, inView = true;
    var motionOK = !reduceMQ.matches;

    // Reference Depth-section scroll parallax on the .wallpapers wrapper.
    // scale 1.12->1 over progress 0..0.5, y 90->-90 over 0..1, opacity 0->1 over 0.05..0.3.
    function updateParallax() {
      if (!motionOK || !wrapper) return;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var r = wrapper.getBoundingClientRect();
      var p = (vh - r.top) / (vh + r.height); // framer offset ['start end','end start']
      if (p < 0) p = 0; else if (p > 1) p = 1;
      var sc = 1.12 + (1 - 1.12) * Math.min(1, p * 2);
      var y = 90 - 180 * p;
      var op = (p - 0.05) / 0.25;
      if (op < 0) op = 0; else if (op > 1) op = 1;
      wrapper.style.transform = "translateY(" + y.toFixed(2) + "px) scale(" + sc.toFixed(4) + ")";
      wrapper.style.opacity = op.toFixed(3);
    }

    function draw() {
      for (var i = 0; i < COUNT; i++) {
        var node = cards[i];
        if (!node) continue;
        // u runs from -8 to +8; card 0 starts at the far-left resting slot (u = -3).
        var u = ((i - 3 - phase) % COUNT + COUNT) % COUNT;
        if (u >= COUNT / 2) u -= COUNT;
        if (Math.abs(u) > VISIBLE_LIMIT) { node.style.visibility = "hidden"; continue; }
        node.style.visibility = "visible";
        node.style.transform = quadMatrix(corners(u), scale, cw, ch);
      }
      updateParallax();
    }

    function tick(t) {
      if (last) phase = (phase + Math.min(t - last, 64) / 1000 * SPEED) % COUNT;
      last = t;
      draw();
      raf = requestAnimationFrame(tick);
    }
    function start() { if (!raf && inView && motionOK) { last = 0; raf = requestAnimationFrame(tick); } }
    function stop() { cancelAnimationFrame(raf); raf = 0; }

    var ro = new ResizeObserver(function () { resize(); draw(); });
    ro.observe(el);

    // Only animate while the row is on screen.
    var io = new IntersectionObserver(function (entries) {
      inView = entries[0].isIntersecting;
      inView ? start() : stop();
    });
    io.observe(el);

    function onMotionPref() {
      motionOK = !reduceMQ.matches;
      if (!motionOK) {
        stop();
        if (wrapper) { wrapper.style.transform = ""; wrapper.style.opacity = ""; }
        draw(); // settle cards at their resting positions, static
      } else {
        start();
      }
    }
    if (reduceMQ.addEventListener) reduceMQ.addEventListener("change", onMotionPref);
    else if (reduceMQ.addListener) reduceMQ.addListener(onMotionPref);

    draw();
    start();

    return function () {
      stop();
      ro.disconnect();
      io.disconnect();
      if (reduceMQ.removeEventListener) reduceMQ.removeEventListener("change", onMotionPref);
      else if (reduceMQ.removeListener) reduceMQ.removeListener(onMotionPref);
    };
  }
})();
